import React, { useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getDoc, doc, setDoc } from "firebase/firestore";
import { auth, db } from './firebase';

// Import components
import LoginForm from './components/LoginForm';
import TasksSidebar from './components/TasksSidebar';
import CalendarView from './components/CalendarView';
import ContractorsSidebar from './components/ContractorsSidebar';
import AssignmentModal from './components/AssignmentModal';
import EventDetailsModal from './components/EventDetailsModal';
import AddContractorModal from './components/AddContractorModal';
import AddUserModal from "./components/AddUserModal";
import ProjectManager from './components/ProjectManager';
import PunchList from './components/PunchList';
import SelectionsForm from './components/SelectionsForm';
import SwornStatementsView from './components/SwornStatementsView';
import FileManager from './components/FileManager';
import NoWorkDaysModal from './components/NoWorkDaysModal';
import ExcelView from './components/ExcelView';

// Import hooks
import useTasks from './hooks/useTasks';
import useContractors from './hooks/useContractors';
import useCalendarEvents from './hooks/useCalendarEvents';
import useProjects from './hooks/useProjects';
import usePunchItems from './hooks/usePunchItems';
import useFiles from './hooks/useFiles';
import { useNoWorkDays, addNoWorkDay, deleteNoWorkDay, isNoWorkDay, getNoWorkDayReason } from './hooks/useNoWorkDays';

// Import styles
import './styles/index.css';

// ========================================
// CUSTOMIZABLE BANNER COMPONENT
// ========================================
const CustomBanner = ({ message, backgroundColor = '#2563eb', textColor = '#ffffff', show = true }) => {
  if (!show || !message) return null;
  
  return (
    <div style={{
      width: '100%',
      backgroundColor: backgroundColor,
      color: textColor,
      padding: '12px 20px',
      textAlign: 'center',
      fontSize: '14px',
      fontWeight: '500',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      {message}
    </div>
  );
};

const useProtection = () => {
  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable copy/cut/paste
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable DevTools shortcuts
    const handleKeyDown = (e) => {
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+J
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (view source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
      // Ctrl+S (save page)
      if (e.ctrlKey && e.keyCode === 83) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCopy);
    document.addEventListener('paste', handleCopy);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCopy);
      document.removeEventListener('paste', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

function App() {
  useProtection();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState("All");
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [userData, setUserData] = useState(null);
  const [durationModal, setDurationModal] = useState(null);
  
  // Navigation state
  const [currentPage, setCurrentPage] = useState('dashboard');

  // ========================================
  // BANNER CONFIGURATION
  // ========================================
  const bannerConfig = {
    show: true,  // Set to false to hide the banner
    message: "Troxell's have fun things to show you! Come visit or tell them when to stop by! 🏡",
    backgroundColor: '#2563eb',  // Blue color (can use hex, rgb, or color names)
    textColor: '#ffffff'  // White text
  };
  
  // Alternative banner examples (uncomment to use):
  
  // Warning banner:
  // const bannerConfig = {
  //   show: true,
  //   message: "⚠️ Scheduled maintenance tonight from 10 PM - 2 AM EST",
  //   backgroundColor: '#dc2626',  // Red
  //   textColor: '#ffffff'
  // };
  
  // Success/info banner:
  // const bannerConfig = {
  //   show: true,
  //   message: "✅ All systems operational - Happy building!",
  //   backgroundColor: '#16a34a',  // Green
  //   textColor: '#ffffff'
  // };

  // Get projects first
  const { 
    projects, 
    currentProject, 
    currentProjectId,
    setCurrentProjectId,
    createProject, 
    deleteProject 
  } = useProjects(user);
  
  const [showProjectManager, setShowProjectManager] = useState(false);

  // Determine which project ID to use based on user role
  const activeProjectId = userRole === 'homeowner' ? userData?.projectId : currentProjectId;

  // Custom hooks for data management
  const { tasks, loading: tasksLoading, error: tasksError, setTasks } = 
    useTasks(user, activeProjectId);
  const { contractors, loading: contractorsLoading, setContractors, deleteContractorById, updateContractorById } = 
    useContractors(user);
  const { events, saveCalendarEvent, updateCalendarEvent, updateCalendarEventEndDate, updateCalendarEventNotes, deleteCalendarEventById, updateCalendarEventsForTask, updateEventColorsForCompletion, refreshEvents } = 
    useCalendarEvents(user, activeProjectId, contractors, tasks);
  const { punchItems, setPunchItems, loading: punchItemsLoading } = 
    usePunchItems(user, activeProjectId);
  const [showNoWorkModal, setShowNoWorkModal] = useState(false);
  const { noWorkDays, loading: noWorkDaysLoading } = useNoWorkDays();

  // Authentication state management
  useEffect(() => {
    const checkUserStatus = async (currentUser) => {
      if (!currentUser) return;
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        // If user document doesn't exist, create it with default homeowner role
        if (!userDoc.exists()) {
          await setDoc(userDocRef, {
            email: currentUser.email,
            active: true,
            role: 'homeowner',
            createdAt: new Date()
          });
          setUserRole('homeowner');
          setUserData({ email: currentUser.email, active: true, role: 'homeowner' });
          return;
        }
        
        const userData = userDoc.data();
        setUserData(userData);
        
        // Check if user is disabled
        if (userData.active === false) {
          await signOut(auth);
          alert("Your account has been disabled. Please contact an administrator.");
          return;
        }
        
        // Set user role
        setUserRole(userData.role || 'homeowner');
      } catch (error) {
        console.error("Error checking user status:", error);
      }
    };
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        checkUserStatus(currentUser);
      } else {
        setUserRole(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Add this useEffect after your other useEffects
  useEffect(() => {
    // Refresh events when navigating to calendar or excel view
    if ((currentPage === 'calendar' || currentPage === 'excel' || currentPage === 'dashboard') && refreshEvents) {
      refreshEvents();
    }
  }, [currentPage]);

  // Function to assign contractor to task
  const assignContractorToTask = async (taskId, contractorId) => {
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('./firebase');
      
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        contractor: contractorId
      });
      
      const task = tasks.find(t => t.id === taskId);
      const taskTitle = task ? task.title : '';
      
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, contractor: contractorId }
          : task
      ));
      
      await updateCalendarEventsForTask(taskId, contractorId, taskTitle);
      setSelectedTask(null);
    } catch (error) {
      console.error("Error assigning contractor:", error);
      alert("Failed to assign contractor");
    }
  };

  // Function to handle new contractor added
  const handleContractorAdded = (newContractor) => {
    setContractors(prev => [...prev, newContractor]);
  };

  // Function to print the calendar with proper multi-day event spanning
  const printCalendar = () => {
    // Print implementation
    window.print();
  };

  // Function to handle navigation between pages
  const handleNavigation = (page) => {
    setCurrentPage(page);
  };

  // Function to update task completion
  const updateTaskCompletion = async (taskId, completed) => {
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('./firebase');
      
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        completed: completed
      });
      
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, completed }
          : task
      ));
      
      await updateEventColorsForCompletion(taskId, completed);
    } catch (error) {
      console.error("Error updating task completion:", error);
      alert("Failed to update task completion");
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Show login form if not authenticated
  if (!user) {
    return <LoginForm />;
  }

  // HOMEOWNER VIEW
  if (userRole === 'homeowner') {
    const homeownerProject = projects.find(p => p.id === userData?.projectId);
    
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Banner at the top */}
        <CustomBanner {...bannerConfig} />
        
        {/* Main content with top padding to account for banner */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0'
        }}>
          <div className="homeowner-header">
            <div className="homeowner-nav-buttons">
              {/* Navigation buttons here if needed */}
            </div>
          </div>
          {userData?.projectId && homeownerProject ? (
            
            <CalendarView
              events={events}
              tasks={tasks}
              contractors={contractors}
              saveCalendarEvent={saveCalendarEvent}
              updateCalendarEvent={updateCalendarEvent}
              setSelectedCalendarEvent={setSelectedCalendarEvent}
              printCalendar={printCalendar}
              setShowAddUser={setShowAddUser}
              currentUser={user}
              isAdmin={false}
              projects={projects} 
              currentProject={homeownerProject}
              onNavigate={setCurrentPage}
              onSelectProject={setCurrentProjectId}
              showManageUsers={showManageUsers}
              setShowManageUsers={setShowManageUsers}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No project assigned. Contact your administrator.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ADMIN VIEW - Navigation between pages
  
  // Render Punch List
  if (currentPage === 'punchlist') {
    return (
      <>
        <CustomBanner {...bannerConfig} />
        <div style={{ marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0' }}>
          <PunchList
            user={user}
            userRole={userRole}
            onNavigate={handleNavigation}
            currentProject={currentProject}
            punchItems={punchItems}
            setPunchItems={setPunchItems}
            projects={projects}
            onProjectChange={setCurrentProjectId}
            contractors={contractors}
          />
        </div>
      </>
    );
  }

  // Render Selections Page for Admin
  if (currentPage === 'selections') {
    return (
      <>
        <CustomBanner {...bannerConfig} />
        <div style={{ marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0' }}>
          <SelectionsForm
            user={user}
            userRole={userRole}
            currentProject={currentProject}
            onNavigate={setCurrentPage}
            db={db}
            projects={projects}
            onProjectChange={setCurrentProjectId}
          />
        </div>
      </>
    );
  }

  // Render Sworn Statements page
  if (currentPage === 'swornstatements') {
    return (
      <>
        <CustomBanner {...bannerConfig} />
        <div style={{ marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0' }}>
          <SwornStatementsView
            user={user}
            userRole={userRole}
            onNavigate={setCurrentPage}
            currentProject={currentProject}
            projects={projects}
            onProjectChange={setCurrentProjectId}
          />
        </div>
      </>
    );
  }

  // Render File Manager page
  if (currentPage === 'files') {
    return (
      <>
        <CustomBanner {...bannerConfig} />
        <div style={{ marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0' }}>
          <FileManager
            user={user}
            userRole={userRole}
            onNavigate={setCurrentPage}
            currentProject={currentProject}
            projects={projects}
            onProjectChange={setCurrentProjectId}
          />
        </div>
      </>
    );
  }

  // Render Line Drive Calendar page
  if (currentPage === 'excel') {
    return (
      <>
        <CustomBanner {...bannerConfig} />
        <div style={{ marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0' }}>
          <ExcelView
            tasks={tasks}
            contractors={contractors}
            events={events}
            user={user}
            userRole={userRole}
            onNavigate={setCurrentPage}
            currentProject={currentProject}
            projects={projects}
            onProjectChange={setCurrentProjectId}
            setTasks={setTasks}
          />
        </div>
      </>
    );
  }

  // Render Calendar (default)
  return (
    <>
      <CustomBanner {...bannerConfig} />
      <div className="app-container" style={{ 
        marginTop: bannerConfig.show && bannerConfig.message ? '48px' : '0'
      }}>
        {/* Tasks Sidebar - Left */}
        <TasksSidebar
          tasks={tasks}
          phaseFilter={phaseFilter}
          setPhaseFilter={setPhaseFilter}
          contractors={contractors}
          setSelectedTask={setSelectedTask}
          currentProject={currentProject}
          projects={projects}
          setCurrentProjectId={setCurrentProjectId}
          createProject={createProject}
          deleteProject={deleteProject}
          events={events}
          setTasks={setTasks}
          currentUser={user}
          isAdmin={true}
        />

        {/* Calendar - Center */}
        <CalendarView
          events={events}
          tasks={tasks}
          contractors={contractors}
          saveCalendarEvent={saveCalendarEvent}
          updateCalendarEvent={updateCalendarEvent}
          setSelectedCalendarEvent={setSelectedCalendarEvent}
          printCalendar={printCalendar}
          setShowAddUser={setShowAddUser}
          currentUser={user}
          isAdmin={true}
          projects={projects} 
          currentProject={currentProject}
          onNavigate={setCurrentPage}
          onSelectProject={setCurrentProjectId}
          showManageUsers={showManageUsers}
          setShowManageUsers={setShowManageUsers}
        />

        {/* Contractors Sidebar - Right */}
        <ContractorsSidebar 
          contractors={contractors}
          loading={loading}
          setShowAddContractor={setShowAddContractor}
          deleteContractor={deleteContractorById}
          updateContractor={updateContractorById}
        />

        {/* Assignment Modal */}
      {selectedTask && (
        <AssignmentModal
          selectedTask={selectedTask}
          contractors={contractors}
          assignContractorToTask={assignContractorToTask}
          setSelectedTask={setSelectedTask}
        />
      )}

      {/* Event Details Modal */}
      {selectedCalendarEvent && !durationModal && (
        <EventDetailsModal
          selectedCalendarEvent={selectedCalendarEvent}
          setSelectedCalendarEvent={setSelectedCalendarEvent}
          updateCalendarEventNotes={updateCalendarEventNotes}
          updateCalendarEventEndDate={updateCalendarEventEndDate}
          updateCalendarEvent={updateCalendarEvent}
          deleteCalendarEventById={deleteCalendarEventById}
          updateTaskCompletion={updateTaskCompletion}
          onChangeEndDate={() => {
            // Close event details modal and open duration modal
            setDurationModal({
              id: selectedCalendarEvent.id,
              task: selectedCalendarEvent.task,
              startDate: selectedCalendarEvent.start,
              endDate: selectedCalendarEvent.end,
              assignedContractor: selectedCalendarEvent.contractor,
              taskTitle: selectedCalendarEvent.task?.title,
              isUpdate: true
            });
            setSelectedCalendarEvent(null);
          }}
        />
      )}
      {/*Add User Modal */}
      {showAddUser && (
        <AddUserModal setShowAddUser={setShowAddUser} />
      )}

      {/* Add Contractor Modal */}
      {showAddContractor && (
        <AddContractorModal
          setShowAddContractor={setShowAddContractor}
          onContractorAdded={handleContractorAdded}
        />
      )}

      {/* Project Manager Modal */}
      {showProjectManager && (
        <ProjectManager
          currentProject={currentProject}
          projects={projects}
          onSelectProject={setCurrentProjectId}
          onCreateProject={createProject}
          onDeleteProject={deleteProject}
          setShowProjectManager={setShowProjectManager}
        />
      )}

      {/* No Work Days Modal */}
      {showNoWorkModal && (
        <NoWorkDaysModal
          noWorkDays={noWorkDays}
          onClose={() => setShowNoWorkModal(false)}
          onAddNoWorkDay={addNoWorkDay}
          onDeleteNoWorkDay={deleteNoWorkDay}
          //showToast={showToast}
        />
      )}
      </div>
    </>
  );
}

export default App;